<template>
  <div class="login-page">
    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <!-- Logo and Brand Header -->
          <div class="brand-header mb-6">
            <router-link to="/">
              <PassingOnLogo class="brand-logo" tone="light" align="center" />
            </router-link>
          </div>

          <!-- Login Form -->
          <v-card v-if="currentStep === 'login'" class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Sign In
            </v-card-title>
            
            <v-card-text class="pa-6">
              <div class="mb-6">
                <p class="text-h6 mb-4 text-center">
                  Welcome back
                </p>
                <p class="text-body-1 text--secondary mb-6 text-center">
                  Sign in to access your memory
                </p>
              </div>

              <v-alert v-if="apiError" type="error" variant="tonal" class="mb-4">
                <div class="d-flex align-center justify-space-between">
                  <span>{{ apiError }}</span>
                  <v-btn
                    v-if="canRetryLogin"
                    color="error"
                    variant="text"
                    size="small"
                    @click="retryLogin"
                    :disabled="loading"
                  >
                    Try Again
                  </v-btn>
                </div>
              </v-alert>

              <v-form ref="loginForm" v-model="formValid" @submit.prevent="handleLogin">
                <v-text-field
                  v-model="email"
                  label="Email address"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  :rules="emailRules"
                  required
                  autofocus
                  class="mb-4"
                ></v-text-field>
                
                <v-text-field
                  v-model="password"
                  label="Password"
                  :type="showPassword ? 'text' : 'password'"
                  variant="outlined"
                  density="comfortable"
                  :rules="passwordRules"
                  required
                  :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append-inner="showPassword = !showPassword"
                  class="mb-4"
                ></v-text-field>
                
                <v-btn
                  type="submit"
                  color="primary"
                  size="large"
                  rounded="0"
                  elevation="0"
                  :disabled="!formValid || loading"
                  :loading="loading"
                  block
                  class="mb-4"
                >
                  Sign In
                </v-btn>
              </v-form>

              <div class="text-center mb-4">
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  @click="showForgotPassword = true"
                >
                  Forgot your password?
                </v-btn>
              </div>

              <div class="text-center">
                <v-btn
                  variant="text"
                  color="primary"
                  @click="goToLanding"
                >
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back to Landing Page
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Memorial Selection -->
          <v-card v-else-if="currentStep === 'selectMemorial'" class="main-card" elevation="0">
            <MemorialSelector
              :memorials="memorials"
              :adminMemorials="adminMemorials"
              :contributions="contributions"
              @select="handleMemorialSelection"
              @selectAdmin="handleAdminMemorialSelection"
              @selectContribution="handleContributionSelection"
            />
            
            <v-card-actions class="pa-6">
              <v-btn
                variant="text"
                @click="goBackToLogin"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back to Sign In
              </v-btn>
              
              <v-spacer></v-spacer>
              
              <v-btn
                variant="text"
                color="primary"
                @click="goToLanding"
              >
                <v-icon left>mdi-home</v-icon>
                Home
              </v-btn>
            </v-card-actions>
          </v-card>

          <!-- Loading Memorial -->
          <v-card v-else-if="currentStep === 'loading'" class="main-card" elevation="0">
            <v-card-text class="text-center pa-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
                class="mb-4"
              ></v-progress-circular>
              <h3 class="text-h5 mb-2">Loading Memory</h3>
              <p class="text-body-1 text--secondary">
                Setting up your memory data...
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Forgot Password Modal -->
    <v-dialog v-model="showForgotPassword" max-width="500" persistent>
      <v-card class="forgot-password-modal">
        <v-card-title class="text-h5 pa-6">
          <v-icon color="primary" class="mr-2">mdi-lock-reset</v-icon>
          Reset Password
        </v-card-title>

        <v-card-text class="pa-6 pt-0">
          <template v-if="!resetEmailSent">
            <p class="text-body-1 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <v-alert v-if="resetError" type="error" variant="tonal" class="mb-4">
              {{ resetError }}
            </v-alert>

            <v-form ref="resetForm" v-model="resetFormValid" @submit.prevent="handleSendResetEmail">
              <v-text-field
                v-model="resetEmail"
                label="Email address"
                type="email"
                variant="outlined"
                density="comfortable"
                :rules="emailRules"
                required
                autofocus
              ></v-text-field>
            </v-form>
          </template>

          <template v-else>
            <v-alert type="success" variant="tonal" class="mb-4">
              <div class="d-flex align-center">
                <span>Password reset email sent!</span>
              </div>
            </v-alert>
            <p class="text-body-1">
              We've sent a password reset link to <strong>{{ resetEmail }}</strong>.
              Please check your inbox and follow the instructions to reset your password.
            </p>
            <p class="text-body-2 text--secondary mt-2">
              The link will expire in 7 days. If you don't see the email, please check your spam folder.
            </p>
          </template>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-btn
            variant="outlined"
            @click="closeForgotPasswordModal"
            :disabled="resetLoading"
          >
            {{ resetEmailSent ? 'Close' : 'Cancel' }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!resetEmailSent"
            color="primary"
            elevation="0"
            :loading="resetLoading"
            :disabled="!resetFormValid || resetLoading"
            @click="handleSendResetEmail"
          >
            Send Reset Link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import authService from '../services/auth'
import MemorialSelector from '../components/MemorialSelector.vue'

const router = useRouter()
const route = useRoute()
const store = useMemorialStore()

// Form refs
const loginForm = ref(null)

// Form validation
const formValid = ref(false)

// Form data
const email = ref('')
const password = ref('')

// UI state
const loading = ref(false)
const showPassword = ref(false)
const apiError = ref('')
const canRetryLogin = ref(false)
const currentStep = ref('login') // 'login', 'selectMemorial', 'loading'
const memorials = ref([])
const adminMemorials = ref([])
const contributions = ref([])

// Store login credentials for retry
const loginCredentials = ref({ email: '', password: '' })

// Forgot password state
const showForgotPassword = ref(false)
const resetEmail = ref('')
const resetFormValid = ref(false)
const resetLoading = ref(false)
const resetError = ref('')
const resetEmailSent = ref(false)
const resetForm = ref(null)

// Validation rules
const emailRules = [
  v => !!v || 'Email is required',
  v => /.+@.+\..+/.test(v) || 'Email must be valid'
]

const passwordRules = [
  v => !!v || 'Password is required'
]

// Methods
const handleLogin = async () => {
  if (!formValid.value) return
  
  loading.value = true
  apiError.value = ''
  canRetryLogin.value = false
  
  // Store credentials for potential retry
  loginCredentials.value = { email: email.value, password: password.value }
  
  try {
    // Use centralized login function to ensure proper authentication state
    const result = await authService.login(email.value, password.value)

    if (result.success) {
      const response = result.response

      // Check if there's a redirect parameter first
      if (route.query.redirect) {
        // If redirect specified, navigate there directly
        loading.value = false
        router.push(route.query.redirect)
        return
      }

      // Check memorial relationships from login response
      const relationships = response.memorialRelationships || []

      // Separate owned vs contributed memorials
      const ownedRelationships = relationships.filter(r => r.relationshipType === 'OWNER')
      const contributedRelationships = relationships.filter(r => r.relationshipType === 'CONTRIBUTOR')

      // If user only has contributions (no owned memorials), redirect based on contribution count
      if (ownedRelationships.length === 0 && contributedRelationships.length > 0) {
        loading.value = false

        if (contributedRelationships.length === 1) {
          // Single contribution - get the contribution details to redirect to the memorial
          await redirectToSingleContribution()
        } else {
          // Multiple contributions - redirect to profile page
          router.push('/profile')
        }
        return
      }

      // Otherwise, use the existing memorial fetching logic for owned memorials
      await fetchMemorials()
    } else {
      throw new Error(result.error || 'Login failed')
    }
  } catch (error) {
    loading.value = false
    const errorInfo = apiService.formatErrorMessage(error)
    console.log('err', error.message);
    apiError.value = errorInfo.message
    canRetryLogin.value = errorInfo.canRetry
    console.error('Login error:', error)
  }
}

const fetchMemorials = async () => {
  try {
    const response = await apiService.listMemorialsAndContributions()

    if (response.success) {
      memorials.value = response.ownedMemorials || []
      adminMemorials.value = response.adminMemorials || []
      contributions.value = response.contributions || []

      const memorialCount = memorials.value.length
      const contributionCount = contributions.value.length
      const adminCount = adminMemorials.value.length
      const totalChoices = memorialCount + contributionCount + adminCount

      if (totalChoices === 0) {
        // No memorials found, redirect to create new one
        loading.value = false
        store.clearMemorialData()
        router.push('/who')
        return
      }

      if (totalChoices === 1) {
        loading.value = false
        if (memorialCount === 1) {
          await loadSingleMemorial(memorials.value[0].uuid)
        } else if (adminCount === 1 && memorialCount === 0) {
          await loadSingleMemorial(adminMemorials.value[0].uuid)
        } else {
          const contribution = contributions.value[0]
          const shareSlug = contribution?.memorial?.shareSlug
          if (shareSlug) {
            router.push(`/for/${shareSlug}`)
          } else {
            router.push('/profile')
          }
        }
        return
      }

      // Multiple choices (2+ memorials, 2+ contributions, admins, or a mix)
      loading.value = false
      currentStep.value = 'selectMemorial'
    } else {
      throw new Error(response.msg || 'Failed to fetch memorials')
    }
  } catch (error) {
    loading.value = false
    const errorInfo = apiService.formatErrorMessage(error)
    apiError.value = `We couldn't load your memorials. ${errorInfo.message}`
    canRetryLogin.value = errorInfo.canRetry
    console.error('Memorial list error:', error)
  }
}


const loadSingleMemorial = async (uuid, isContributor = false) => {
  try {
    const response = await apiService.getMemorial(uuid)

    if (response.success && response.memorial) {
      const memorial = response.memorial

      // Load memorial data into store
      store.loadMemorialData(memorial)

      loading.value = false

      // Check if there's a redirect parameter
      if (route.query.redirect) {
        router.push(route.query.redirect)
      } else if (isContributor && memorial.shareSlug) {
        // If user is a contributor (not owner), redirect to public memorial view
        router.push(`/for/${memorial.shareSlug}`)
      } else {
        // Navigate based on payment status for owners
        const isPaid = typeof memorial.paymentCompleted === 'boolean' ? memorial.paymentCompleted : Boolean(memorial.stripeCustomerId)
        if (!isPaid) {
          // Resume the highest reached step before payment
          const targetStep = Math.min(store.maxReachedStep || 1, 6)
          const routes = ['/', '/who', '/photos', '/message', '/preview', '/save', '/payment', '/contacts', '/share']
          store.goToStep(targetStep)
          router.push(routes[targetStep] || '/payment')
        } else {
          // Memorial is complete and paid, go to feed
          router.push('/memory/feed')
        }
      }
    } else {
      throw new Error(response.msg || 'Failed to load memory')
    }
  } catch (error) {
    loading.value = false
    const errorInfo = apiService.formatErrorMessage(error)
    apiError.value = `We couldn't load your memory. ${errorInfo.message}`
    canRetryLogin.value = errorInfo.canRetry
    console.error('Memorial load error:', error)
  }
}

const redirectToSingleContribution = async () => {
  try {
    // Get the user's contributions to find the shareSlug
    const response = await apiService.listMemorialsAndContributions()

    if (response.success && response.contributions && response.contributions.length > 0) {
      const contribution = response.contributions[0]

      if (contribution.memorial && contribution.memorial.shareSlug) {
        // Redirect to the public memorial view
        router.push(`/for/${contribution.memorial.shareSlug}`)
      } else {
        // Fallback to profile if shareSlug is not available
        router.push('/profile')
      }
    } else {
      // Fallback to profile if no contributions found
      router.push('/profile')
    }
  } catch (error) {
    console.error('Error redirecting to contribution:', error)
    // Fallback to profile on error
    router.push('/profile')
  }
}

const handleMemorialSelection = async (memorial) => {
  currentStep.value = 'loading'
  await loadSingleMemorial(memorial.uuid)
}

const handleAdminMemorialSelection = async (memorial) => {
  currentStep.value = 'loading'
  await loadSingleMemorial(memorial.uuid)
}

const handleContributionSelection = async (contribution) => {
  const shareSlug = contribution.memorial?.shareSlug
  if (shareSlug) {
    router.push(`/for/${shareSlug}`)
  } else {
    router.push('/profile')
  }
}

const retryLogin = async () => {
  email.value = loginCredentials.value.email
  password.value = loginCredentials.value.password
  await handleLogin()
}

const goBackToLogin = () => {
  currentStep.value = 'login'
  apiError.value = ''
  canRetryLogin.value = false
}

const goToLanding = () => {
  router.push('/')
}

// Forgot password methods
const handleSendResetEmail = async () => {
  if (!resetFormValid.value) return

  resetLoading.value = true
  resetError.value = ''

  try {
    const response = await apiService.sendPasswordResetEmail(resetEmail.value)

    if (response.success) {
      resetEmailSent.value = true
    } else {
      resetError.value = response.msg || 'Failed to send reset email. Please try again.'
    }
  } catch (error) {
    console.error('Password reset error:', error)
    const errorInfo = apiService.formatErrorMessage(error)
    resetError.value = errorInfo.message
  } finally {
    resetLoading.value = false
  }
}

const closeForgotPasswordModal = () => {
  showForgotPassword.value = false
  // Reset the modal state after a short delay to allow animation
  setTimeout(() => {
    resetEmail.value = ''
    resetError.value = ''
    resetEmailSent.value = false
  }, 300)
}

// Check for message from query params on mount
onMounted(() => {
  if (route.query.msg) {
    apiError.value = route.query.msg
  }
  if (route.query.reset === '1' || route.query.reset === 'true') {
    showForgotPassword.value = true
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;

.login-page {
  @include page-container;
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: #1C1C1C;
}

.brand-header {
  text-align: center;

  a {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
  }
}

.brand-logo {
  height: 50px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 600;
  color: white;
  margin: 0;
}

.main-card {
  @include main-card;

  .v-card-title {
    @include section-header;
  }
}

@include mobile {
  .pa-6 {
    padding: 1rem !important;
  }
}

.forgot-password-modal {
  border-radius: 8px;

  .v-card-title {
    display: flex;
    align-items: center;
  }
}
</style>
